<template>
  <Container :class="$style.container">
    <ButtonBack
      text="Dashboard"
      :to="$options.homeRoute"
    />
    <Container
      :class="$style.innerContainer"
      data-test-id="ViewAll__container"
    >
      <div :class="$style.headerStyle">
        <HeadingMedium>
          {{ displayName }} {{ totalCountDisplay }}
        </HeadingMedium>
        <Icon
          :class="iconColor"
          :icon="widgetIcon"
          :scale="1.5"
        />
      </div>
      <ItemListPagination
        :columns="tableColumns"
        :currentPageNum="currentPageNum"
        :indeterminateSelectAll="indeterminateSelectAll"
        :isLoading="isListLoading"
        :itemComponent="$options.RowData"
        :rowsPerPage="10"
        :sortBy="sortBy"
        :sortDirection="sortDirection"
        :tableData="tableDataFormatted"
        :totalCount="totalCount"
        @pageChange="handlePageChange"
        @sortColumn="handleSortColumn"
      >
        <template v-slot:toolbar>
          <HomeSearchBar
            :count="totalCount"
            :disabled="false"
            searchPlaceholder="Search"
            :shouldFocusSearch="shouldFocusSearch"
            :toolBarDisplayName="displayName"
            @triggerSearch="handleEnterSearchTerm"
          />
        </template>
      </ItemListPagination>
    </Container>
  </Container>
</template>
<script>
import {
  ButtonBack,
  Constants,
  Container,
  HeadingMedium,
  Icon,
  ItemListPagination,
  Types,
} from '@jumpcloud/ui-components';
import { ItemTypeConsts, ItemTypeRoutes, StoreModuleMap } from 'client/util/Constants';
import { mapActions, mapState } from 'vuex';
import DateUtil from 'client/util/DateUtil';
import HomeSearchBar from 'client/HomePageV2/components/HomeSearchBar.vue';
import HomeWidgetConsts from 'client/HomePageV2/constants/HomeWidgetConsts';
import InsightsEventsApi from 'client/api/v2/InsightsEventsApi';
import JobApi from 'client/api/v2/JobApi';
import RowData from 'client/HomePageV2/components/RowData.vue';
import SystemUserApi from 'client/api/v2/SystemUserApi';
import UserStateFilters from 'client/HomePageV2/constants/UserStateFilters';
import ViewAllMixin from 'client/HomePageV2/mixins/ViewAllMixin';

const { Column } = Types;
const {
  apiPayloadConst,
  widgetTableColumns,
} = HomeWidgetConsts;
const { maxFetchLimit } = apiPayloadConst;
const { sortDirections } = Constants;
const administratorModel = 'AdministratorModel';
const userDisplayPreferencesModule = StoreModuleMap[ItemTypeConsts.userDisplayPreferences];

export default {
  name: 'ViewAllWithoutActions',

  components: {
    ButtonBack,
    Container,
    HeadingMedium,
    HomeSearchBar,
    Icon,
    ItemListPagination,
  },

  homeRoute: ItemTypeRoutes[ItemTypeConsts.home],

  mixins: [
    ViewAllMixin,
  ],

  data() {
    return {
      showActions: false,
      sortBy: 'scheduleDate',
      tableDataFormatted: [],
      UsersState: [],
      UsersStateGroupMembership: [],
      counter: 0,
      widgetName: this.$route.params.widgetName,
      groupId: this.$route.params.id,
    };
  },

  RowData,

  computed: {
    ...mapState(userDisplayPreferencesModule, {
      displaySettings: state => state.displayPreferencesData,
    }),

    ...mapState(administratorModel, [
      'resetKey',
    ]),

    iconColor() {
      if (HomeWidgetConsts.widgetIcons[this.widgetName] === 'error' && !this.totalCount) { return this.$style.colorSuccess; }
      if (HomeWidgetConsts.widgetIcons[this.widgetName] === 'error' && this.totalCount > 0) { return this.$style.colorWarning; }
      return this.$style.colorInfo;
    },

    startTime() {
      const timeStamp = new Date();
      timeStamp.setDate(timeStamp.getDate() - 1);
      return timeStamp.toISOString();
    },

    tableColumns() {
      return widgetTableColumns[this.widgetName].map(column => Column(column));
    },

    userStateFilter() {
      let filter;
      switch (this.widgetName) {
        case 'ScheduledUserSuspensions':
          filter = UserStateFilters.state(
            'SUSPENDED',
            this.displaySettings.scheduledUserSuspensions.numberOfDaysToShow,
          );
          break;
        case 'ScheduledUserActivations':
          filter = UserStateFilters.state(
            'ACTIVATED',
            this.displaySettings.scheduledUserActivations.numberOfDaysToShow,
          );
          break;
        default:
          break;
      }

      return filter;
    },

    widgetIcon() {
      if (HomeWidgetConsts.widgetIcons[this.widgetName] === 'error' && !this.totalCount) { return 'checkmarkCircle'; }
      return HomeWidgetConsts.widgetIcons[this.widgetName];
    },

  },

  watch: {
    tableData: {
      handler() {
        this.handleTableDataFormatted();
      },
    },
  },

  created() {
    if (this.widgetName === 'manualGroupMembership') {
      this.fetchTableData();
    } else {
      this.fetchUserState();
    }
  },

  methods: {
    ...mapActions(userDisplayPreferencesModule, [
      'fetchDisplayPreferencesData',
    ]),

    async fetchData() {
      this.isListLoading = true;
      const callApi = async (page = 0) => JobApi.getUserState(
        page * maxFetchLimit, maxFetchLimit, this.userStateFilter,
      );

      // Initial request will be equal to the response of callAPI
      const initialRequest = await callApi();
      let apps = initialRequest.results;
      let { totalCount } = initialRequest;

      if (totalCount > maxFetchLimit) {
        const pagedRequests = [];
        for (let i = 1; totalCount > maxFetchLimit; totalCount -= maxFetchLimit, i += 1) {
          pagedRequests.push(callApi(i));
        }

        /* Using Promise.all here is preferred to awaiting in the for loop
           due to a significant performance improvement
        */
        const responses = await Promise.all(pagedRequests);

        // Initialize with initialRequest.results so it is included with the aggregated responses
        apps = responses
          .map(resp => resp.results)
          .reduce((all, current) => all.concat(current), initialRequest.results);
      }

      this.data = apps;
      this.UsersState = this.data.map(state => ({
        scheduleDate: state.scheduledDate,
        id: state.systemUserId,
      }));
      this.totalCount = this.data.length;
      this.isListLoading = false;

      return this.UsersState;
    },

    async fetchUserManualGroup(filter) {
      let count = filter.length;

      const callUsersApi = async (page = 0) => {
        const filters = await this.getFilters(filter, page);
        return SystemUserApi.fetch(
          null, null, null, null, null, filters,
        );
      };

      const initialRequest = await callUsersApi();
      let apps = initialRequest.results;

      if (count > maxFetchLimit) {
        const pagedRequests = [];
        for (let i = 1; count > maxFetchLimit; count -= maxFetchLimit, i += 1) {
          pagedRequests.push(callUsersApi(i));
        }

        /* Using Promise.all here is preferred to awaiting in the for loop
         due to a significant performance improvement
        */
        const responses = await Promise.all(pagedRequests);

        /* Initialize with initialRequest.results
          so it is included with the aggregated responses
        */
        apps = responses
          .map(resp => resp.results)
          .reduce((all, current) => all.concat(current), initialRequest.results);
      }

      this.data = apps;
      return this.data;
    },

    async fetchUserState() {
      await this.fetchDisplayPreferencesData();
      const userState = await this.fetchData();
      const response = await this.fetchUsers(userState);
      if (response.length > 0) {
        this.data = await this.filterUserWithState(response, userState);
      } else {
        this.data = [];
      }

      let start = this.currentPageNum * this.rowsPerPage;
      let end = start + this.rowsPerPage;
      this.tableData = this.data.slice(start, end);
      if (!this.tableData.length) {
        if (this.data.length) {
          start = (this.currentPageNum - 1) * this.rowsPerPage;
          end = start + this.rowsPerPage;
          this.currentPageNum -= 1;
          this.tableData = this.data.slice(start, end);
        } else {
          this.currentPageNum = 0;
        }
      }
      await this.handleSortColumn({
        dataFieldName: this.sortBy,
        sortDirection: sortDirections.descending,
      });
      this.selectAll = false;
      this.indeterminateSelectAll = false;
    },

    async fetchUsers(filter) {
      this.isListLoading = true;
      let count = this.totalCount;
      const callUsersApi = async (page = 0) => {
        const filters = await this.getFilters(filter, page);
        return SystemUserApi.fetch(
          null, null, null, null, null, filters,
        );
      };

      const initialRequest = await callUsersApi();
      let apps = initialRequest.results;

      if (count > maxFetchLimit) {
        const pagedRequests = [];
        for (let i = 1; count > maxFetchLimit; count -= maxFetchLimit, i += 1) {
          pagedRequests.push(callUsersApi(i));
        }

        /* Using Promise.all here is preferred to awaiting in the for loop
         due to a significant performance improvement
        */
        const responses = await Promise.all(pagedRequests);

        /* Initialize with initialRequest.results
          so it is included with the aggregated responses
        */
        apps = responses
          .map(resp => resp.results)
          .reduce((all, current) => all.concat(current), initialRequest.results);
      }
      this.data = apps;
      this.isListLoading = false;

      return this.data;
    },

    async filterUserGroupMembership(manualGroupData, userDetails) {
      const res = [];

      for (let i = 0; i < manualGroupData.length; i += 1) {
        for (let j = 0; j < userDetails.length; j += 1) {
          if (manualGroupData[i].user_id === userDetails[j].id) {
            this.counter += 1;
            res.push({
              group: manualGroupData[i].group,
              user_name: userDetails[j].username,
              user_state: userDetails[j].state,
              email: userDetails[j].email,
              timestamp: manualGroupData[i].timestamp,
              user_group_action_from: manualGroupData[i].user_group_action_from,
              action: manualGroupData[i].action,
              user_id: manualGroupData[i].user_id,
              id: this.counter,
              group_id: manualGroupData[i].group_id,
            });
            break;
          }
        }
      }
      this.totalCount = res.length;
      return res;
    },

    async filterUserWithState(arr1, arr2) {
      const res = [];
      arr1.forEach(el1 => arr2.forEach(el2 => {
        if (el2.id === el1.id) {
          res.push({
            userName: el1.username,
            lastName: this.getDisplayName(el1),
            state: this.getState(el1.state),
            email: el1.email,
            scheduleDate: el2.scheduleDate,
          });
        }
      }));

      return res;
    },

    getActionType(op) {
      switch (op) {
        case 'add':
          return 'Remove';
        case 'remove':
          return 'Add';
        default:
          return '';
      }
    },

    async getFilters(filter, page) {
      /* we can pass only 100 Id's at a time so
      we are slicing the array of ids and joining them to pass as filter
      */
      const ids = await filter.map(user => user.id)
        .slice(page * maxFetchLimit, (page * maxFetchLimit) + maxFetchLimit)
        .join('|');
      const filters = await UserStateFilters.getId(ids);

      return filters;
    },

    async getManualMembershipData() {
      const requestOptions = this.getRequestMultiOptions();
      const callApi = (page = 0) => InsightsEventsApi.fetchMultiDistinct({
        ...requestOptions,
        from: page * maxFetchLimit,
        limit: (page * maxFetchLimit) + maxFetchLimit,
        fields: [
          'association.connection.from.object_id',
          'association.connection.to.object_id',
        ],
      });

      const initialRequest = await callApi();
      let apps = initialRequest.data.buckets;
      let totalCount = parseInt(initialRequest.headers['x-total-count'], 10);

      if (totalCount > maxFetchLimit) {
        const pagedRequests = [];
        for (let i = 1; totalCount > maxFetchLimit; totalCount -= maxFetchLimit, i += 1) {
          pagedRequests.push(callApi(i));
        }

        const responses = await Promise.all(pagedRequests);
        apps = responses
          .map(resp => resp.data.buckets)
          .reduce((all, current) => all.concat(current), initialRequest.data.buckets);
      }

      this.data = apps;
      return apps;
    },

    getRequestMultiOptions() {
      const service = ['directory'];
      return {
        service,
        start_time: this.startTime,
        search_term: {
          and: [
            {
              'association.connection.from.type': 'user_group',
            },
            {
              'association.connection.to.type': 'user',
            },
            {
              'association.connection.from.object_id': this.groupId,
            },
          ],
        },
        sort: 'DESC',
        sort_by: 'timestamp',
        include_agg_fields: [
          'association',
          'timestamp',
        ],
      };
    },

    getState(state) {
      switch (state) {
        case 'ACTIVATED':
          return 'Active';
        case 'STAGED':
          return 'Staged';
        case 'SUSPENDED':
          return 'Suspended';
        default:
          return '';
      }
    },

    async handleCreated() {
      this.isListLoading = true;
      const userState = await this.fetchData();
      const response = await this.fetchUsers(userState);
      this.data = await this.filterUserWithState(response, userState);

      let start = this.currentPageNum * this.rowsPerPage;
      let end = start + this.rowsPerPage;
      this.tableData = this.data.slice(start, end);
      this.totalCount = this.data.length;
      if (!this.tableData.length) {
        if (this.data.length) {
          start = (this.currentPageNum - 1) * this.rowsPerPage;
          end = start + this.rowsPerPage;
          this.currentPageNum -= 1;
          this.tableData = this.data.slice(start, end);
        } else {
          this.currentPageNum = 0;
        }
      }
      await this.handleSortColumn({
        dataFieldName: this.sortBy,
        sortDirection: sortDirections.descending,
      });
      this.selectAll = false;
      this.indeterminateSelectAll = false;
    },

    async handleManualGroupMembership() {
      await this.fetchDisplayPreferencesData();
      this.isListLoading = true;
      /* We are getting the user group updates data from ES,
      and since it takes some time for the ES logs to be updated the new/updated
      data was not getting reflected in the investigative page after the remove/add
      action was performed. Hence we've added a delay of 8 seconds which is the time it
      takes to updated the ES logs. */
      await new Promise((resolve) => setTimeout(resolve, 8000));
      let manualGroupData = await this.getManualMembershipData();
      manualGroupData = manualGroupData
        .map((user) => ({
          group: user.data.association.connection.from.name,
          timestamp: DateUtil.formatDateTime(user.data.timestamp),
          user_group_action_from: user.data.association.op,
          action: this.getActionType(user.data.association.op),
          user_id: user.data.association.connection.to.object_id,
          group_id: user.data.association.connection.from.object_id,
        }));

      // get list of user ids from response
      this.UsersStateGroupMembership = manualGroupData.map(item => ({
        id: item.user_id,
      }));

      // fetch users from the list above list
      const userDetails = await this.fetchUserManualGroup(this.UsersStateGroupMembership);
      if (userDetails.length > 0) {
        this.data = await this.filterUserGroupMembership(manualGroupData, userDetails);
      } else {
        this.data = [];
      }

      let start = this.currentPageNum * this.rowsPerPage;
      let end = start + this.rowsPerPage;
      this.tableData = this.data.slice(start, end);
      if (!this.tableData.length) {
        if (this.data.length) {
          start = (this.currentPageNum - 1) * this.rowsPerPage;
          end = start + this.rowsPerPage;
          this.currentPageNum -= 1;
          this.tableData = this.data.slice(start, end);
        } else {
          this.currentPageNum = 0;
        }
      }
      this.tableData = this.data.slice(start, end);
      await this.handleSortColumn({
        dataFieldName: this.sortBy,
        sortDirection: sortDirections.descending,
      });
      this.selectAll = false;
      this.indeterminateSelectAll = false;
      this.isListLoading = false;
    },

    handleTableDataFormatted() {
      this.tableDataFormatted = this.tableData
        .map(user => ({ ...user, scheduleDate: DateUtil.formatDateTime(user.scheduleDate) }));
    },
  },
};
</script>
<style module>
.container {
  background-color: var(--jc-color-disabled);
  display: inherit;
  height: 100%;
  padding: var(--jc-spacer-x-small);
}

.innerContainer {
  background-color: var(--jc-color-surface-light);
  margin-top: var(--jc-spacer);
  width: 100%;
}

.colorWarning {
  color: var(--jc-color-warning-strong);
  padding-left: var(--jc-spacer-small);
  padding-top: var(--jc-spacer-x-small);
}

.colorSuccess {
  color: var(--jc-color-success-base);
  padding-left: var(--jc-spacer-small);
  padding-top: var(--jc-spacer-x-small);
}

.colorInfo {
  color: var(--jc-color-informational-base);
  padding-left: var(--jc-spacer-small);
  padding-top: var(--jc-spacer-x-small);
}

.headerStyle {
  display: flex;
  width: 90%;
}

.list {
  padding-left: var(--jc-spacer);
}

.dropdownClass {
  float: right;
}
</style>
